import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Layout, Table, Input, Row, Col, Tag, message, Space, Checkbox, Typography } from 'antd';
import Button from '@mui/material/Button';
import { debounce } from 'lodash';
import { DoubleLine } from "../../../components/Labels";
import { portfolioTypeString } from "../../../utils/strings";

const { Content, Footer } = Layout;

const SelectedItem = ({ portfolio, onRemove }) => {
  return (
    <Tag
      style={{
        padding: '5px 10px',
        borderRadius: '4px',
        alignItems: 'center',
        background: '#fff',
        marginBottom: '5px',
      }}
      closable
      onClose={(e) => {
        e.preventDefault();
        onRemove(portfolio.id);
      }}
    >
      <Typography.Text ellipsis={{rows: 1}} style={{maxWidth: 150}}>{portfolio?.name ?? ''}</Typography.Text>
    </Tag>
  );
};

const PortfolioSearch = ({ onSelect, initialSelectedPortfolios = [], allowTypes = ['b', 's', 'm'],
                             maxSelected = 20 }) => {
  const portfolios = useSelector(state => state.portfolios.items);
  const [selectedRowKeys, setSelectedRowKeys] = useState(initialSelectedPortfolios.map(p => p.id));
  const [selectedPortfolios, setSelectedPortfolios] = useState(initialSelectedPortfolios);
  const [searchValue, setSearchValue] = useState("");
  const [selectedTypes, setSelectedTypes] = useState(allowTypes);

  const filteredPortfolios = useMemo(() => {
    return portfolios.filter(portfolio =>
      (selectedTypes.length === 0 || selectedTypes.includes(portfolio.type)) &&
      (!searchValue || portfolio?.name?.toLowerCase().includes(searchValue.toLowerCase()))
    );
  }, [portfolios, selectedTypes, searchValue]);

  const onSelectChange = useCallback((newRowKeys, newRows) => {
    if (newRowKeys.length > maxSelected) {
      message.warning(`You can only select up to ${maxSelected} portfolios.`);
      return;
    }
    setSelectedRowKeys(newRowKeys);
    setSelectedPortfolios(newRows);
  }, [maxSelected]);

  const handleSearch = useCallback(debounce((value) => {
    setSearchValue(value || "");
  }, 300), []);

  const handleTypeChange = (checkedValues) => {
    if (checkedValues.includes("all")) {
      checkedValues = ["b", "s", "m"].filter((type) => allowTypes.includes(type));
    }
    setSelectedTypes(checkedValues);
  };

  const handleSubmit = useCallback(() => {
    onSelect(selectedPortfolios);
  }, [onSelect, selectedPortfolios]);

  const handleTagClose = useCallback((portfolioId) => {
    const newSelectedPortfolios = selectedPortfolios.filter(p => p.id !== portfolioId);
    setSelectedPortfolios(newSelectedPortfolios);
    setSelectedRowKeys(newSelectedPortfolios.map(p => p.id));
  }, [selectedPortfolios]);

  const clearAllSelected = () => {
    setSelectedRowKeys([]);
    setSelectedPortfolios([]);
    onSelect([]);
  };

  const columns = [
    {
      title: '',
      key: 'checkbox',
      width: 50,
      render: () => null, // This column is for checkbox, render nothing here
    },
    {
      title: 'Portfolio',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <DoubleLine
          title={record.name}
          text={portfolioTypeString(record.type)}
        />
      )
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <Layout style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Content style={{ padding: '20px', height: '60vh' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '15px' }}>
          <Input
            placeholder="Search Portfolios by Name"
            onChange={(e) => handleSearch(e.target.value)}
            size="large"
            style={{ marginBottom: '15px' }}
          />
        </Row>
        <Row wrap={false} align={'top'}>
          <Col flex={'0 0 120px'}>
            <Checkbox.Group
              options={[
                { label: 'Blocks', value: 'b' },
                { label: 'Sleeves', value: 's' },
                { label: 'Models', value: 'm' },
                { label: "All", value: "all" }
              ]}
              direction={'vertical'}
              value={selectedTypes}
              onChange={handleTypeChange}
              style={{ marginBottom: '15px' }}
            />
          </Col>
          <Col flex={'1'}>
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={filteredPortfolios}
              rowKey="id"
              scroll={{ y: '40vh' }}
              size="middle"
              pagination={{ pageSize: 10, position: ['bottomRight'] }}
            />
          </Col>
        </Row>
      </Content>
      <Footer className={'bg-gradient-to-b from-gray-100 to-zinc-50'} style={{
        position: 'relative',
        bottom: 0,
        width: '100%',
        backgroundColor: '#ededed',
        padding: '10px 20px',
        boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <Space direction={'vertical'} style={{width: '100%'}}>
          <Space>
            <Button variant="contained" color="info" onClick={handleSubmit}>
              Continue ({selectedPortfolios.length})
            </Button>
            <Button variant="outlined" color={'error'} onClick={clearAllSelected}>
              Clear All
            </Button>
          </Space>
          <Space wrap>
            {selectedPortfolios.map(portfolio => (
              <SelectedItem key={portfolio.id} portfolio={portfolio} onRemove={handleTagClose} />
            ))}
          </Space>
        </Space>
      </Footer>
    </Layout>
  );
};

export default PortfolioSearch;